import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Location } from '@/core/api/entities/location'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useDeleteLocation } from '@/core/hooks/useLocation'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

interface UseLocationDeletionProps {
	onSuccess?: () => void
	onError?: (error: Error) => void
}

export const useLocationDeletion = (props?: UseLocationDeletionProps) => {
	const { onSuccess, onError } = props || {}
	const { showNotification } = useNotificationContext()
	const { t } = useTranslation('common')
	const [selectedLocation, setSelectedLocation] = useState<Location | null>(
		null
	)
	const [isModalVisible, setIsModalVisible] = useState(false)

	const { mutate, isPending } = useDeleteLocation({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				if (selectedLocation?.id) {
					// Show success notification
					showNotification({
						type: 'success',
						message: t('entities.location.delete.success.title'),
						description: t('entities.location.delete.success.description', {
							name: selectedLocation.name,
						}),
					})

					// Close modal
					setIsModalVisible(false)
					setSelectedLocation(null)

					// Call onSuccess callback if provided
					if (onSuccess) onSuccess()
				}
			},
			onError: (error: Error) => {
				// Show error notification
				showNotification({
					type: 'error',
					message: t('entities.location.delete.error.title'),
					description: t('entities.location.delete.error.description', {
						name: selectedLocation?.name,
					}),
				})

				// Call onError callback if provided
				if (onError) onError(error)
			},
		},
	})

	const openDeleteModal = (location: Location) => {
		setSelectedLocation(location)
		setIsModalVisible(true)
	}

	const closeDeleteModal = () => {
		setIsModalVisible(false)
		setSelectedLocation(null)
	}

	const handleDeleteLocation = () => {
		if (selectedLocation?.id) {
			mutate({
				locationId: selectedLocation.id,
				data: {}, // Empty object as DeleteLocationRequestDTO is empty
			})
		}
	}

	return {
		selectedLocation,
		isModalVisible,
		isDeleting: isPending,
		openDeleteModal,
		closeDeleteModal,
		handleDeleteLocation,
	}
}
